import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Grid, TextField, Button, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, Chip, TablePagination } from '@mui/material';
import api from '../../api';

const ApplyLeaveTab = ({ isStatusOnly, empId, approvedNHDates = [], showMsg, onSuccess, myRequests = [] }) => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const [formData, setFormData] = useState({ leave_type: '', start_date: '', end_date: '', days: 0, reason: '' });

  useEffect(() => {
    if (formData.start_date && formData.end_date) {
      const start = new Date(formData.start_date);
      const end = new Date(formData.end_date);
      setFormData(prev => ({ ...prev, days: end >= start ? Math.ceil(Math.abs(end - start) / (1000 * 60 * 60 * 24)) + 1 : 0 }));
    }
  }, [formData.start_date, formData.end_date]);

  const handleApplySubmit = async (e) => {
    e.preventDefault();
    if (formData.days <= 0) return showMsg("Invalid date range", "error");
    try {
      await api.post(`/leave/apply/${empId}`, formData);
      showMsg(`${formData.leave_type} request submitted successfully!`);
      setFormData({ leave_type: '', start_date: '', end_date: '', days: 0, reason: '' });
      if (onSuccess) onSuccess();
    } catch (err) { showMsg(err.response?.data?.detail || "Failed to apply", "error"); }
  };

  if (isStatusOnly) {
    return (
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, textAlign: 'left' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>My Leave Applications</Typography>
          <TextField select size="small" value={selectedYear} onChange={e => setSelectedYear(e.target.value)} sx={{ minWidth: 120 }}>
            {[currentYear, currentYear - 1, currentYear - 2].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </TextField>
        </Box>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f8fafc' }}><TableRow><TableCell>Type</TableCell><TableCell>Duration</TableCell><TableCell>Days</TableCell><TableCell>Reason</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
          <TableBody>
            {myRequests.filter(req => req.start_date.startsWith(selectedYear.toString())).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(req => (
              <TableRow key={req.id}>
                <TableCell sx={{ fontWeight: 'bold' }}>{req.leave_type}</TableCell><TableCell>{req.start_date} to {req.end_date}</TableCell><TableCell>{req.days}</TableCell><TableCell>{req.reason}</TableCell>
                <TableCell><Chip size="small" label={req.status} color={req.status.includes('PENDING') ? 'warning' : req.status === 'APPROVED' ? 'success' : 'error'} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination component="div" count={myRequests.length} page={page} onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={e => {setRowsPerPage(parseInt(e.target.value, 10)); setPage(0);}} />
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 4, borderRadius: 2, textAlign: 'left' }}>
      <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 'bold', mb: 3 }}>Apply for Leave</Typography>
      <Box component="form" onSubmit={handleApplySubmit} sx={{ width: '100%' }}>
        <Grid container spacing={3} alignItems="flex-start">
          
          <Grid item xs={12} sm={4} md={3}>
            <TextField select fullWidth size="medium" label="Leave Type" required value={formData.leave_type} onChange={e => setFormData({...formData, leave_type: e.target.value})} sx={{ minWidth: 200 }}>
              {['CL', 'ML', 'AL', 'EL', 'NH', 'LOP', 'CO'].map(lt => <MenuItem key={lt} value={lt}>{lt === 'NH' ? 'National Holiday (NH)' : lt === 'LOP' ? 'Loss of Pay (LOP)' : lt === 'CO' ? 'Compensation Off (CO)' : lt}</MenuItem>)}
            </TextField>
          </Grid>

          {formData.leave_type === 'NH' ? (
            <>
              <Grid item xs={12} sm={5} md={4}>
                <TextField select fullWidth size="medium" label="Select Approved Holiday" required value={formData.start_date} onChange={e => setFormData({...formData, start_date: e.target.value, end_date: e.target.value, days: 1})}>
                  {approvedNHDates.length === 0 && <MenuItem disabled value="">No Available Holidays</MenuItem>}
                  {approvedNHDates.map(nh => <MenuItem key={nh.date} value={nh.date}>{nh.date} - {nh.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3} md={2}><TextField fullWidth size="medium" label="Total Days" value={1} InputProps={{ readOnly: true }} sx={{ bgcolor: '#f8fafc' }} /></Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} sm={4} md={3}><TextField fullWidth size="medium" type="date" label="Start Date" required InputLabelProps={{ shrink: true }} value={formData.start_date} onChange={e => setFormData({...formData, start_date: e.target.value})} /></Grid>
              <Grid item xs={12} sm={4} md={3}><TextField fullWidth size="medium" type="date" label="End Date" required InputLabelProps={{ shrink: true }} value={formData.end_date} onChange={e => setFormData({...formData, end_date: e.target.value})} /></Grid>
              <Grid item xs={12} sm={2} md={1}><TextField fullWidth size="medium" label="Days" value={formData.days} InputProps={{ readOnly: true }} sx={{ bgcolor: '#f8fafc' }} /></Grid>
            </>
          )}
          
          <Grid item xs={12} md={9}><TextField size="medium" fullWidth label="Reason for Leave" required value={formData.reason} onChange={e => setFormData({...formData, reason: e.target.value})} /></Grid>
          <Grid item xs={12} md={3}><Button type="submit" variant="contained" fullWidth sx={{ height: '56px' }}>Submit Request</Button></Grid>
        
        </Grid>
      </Box>
    </Paper>
  );
};

export default ApplyLeaveTab;